<template>
    <!-- Navbar already shows pageTitle — no in-page h1 -->
    <div class="reimbursement-edit-page">
    <b-card>
        <div v-if="loading" class="text-center text-muted py-2">Loading...</div>
        <b-alert v-else-if="loadError" show variant="danger">{{ loadError }}</b-alert>
        <validation-observer v-else ref="reimbursementRules">
            <b-form @submit.prevent>
                <b-row>
                    <b-col cols="12" md="4">
                        <b-form-group label-for="item-name">
                            <template #label>
                                Item / Title
                                <span v-if="isFieldRequired('itemName')" class="text-danger">*</span>
                            </template>
                            <validation-provider
                                #default="{ errors }"
                                name="Item / Title"
                                :rules="isFieldRequired('itemName') ? 'required' : ''"
                            >
                                <b-form-input
                                    id="item-name"
                                    v-model="reimbursement.itemName"
                                    placeholder="e.g. Office supplies, Software license"
                                    :state="itemTitleError || errors.length > 0 ? false : null"
                                />
                                <small class="text-danger">{{ itemTitleError || errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Account / Client *" label-for="account-name">
                            <validation-provider
                                #default="{ errors }"
                                name="Account / Client"
                                :rules="isFieldRequired('accountName') ? 'required' : ''"
                            >
                                <v-select
                                    id="account-name"
                                    v-model="reimbursement.accountName"
                                    :options="accountsList"
                                    label="title"
                                    :reduce="(option) => option.title"
                                    :state="errors.length > 0 ? false:null"
                                    placeholder="Select Account / Client"
                                    @open="ensureClients"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Vertical *" label-for="vertical">
                            <validation-provider
                                #default="{ errors }"
                                name="Vertical"
                                :rules="isFieldRequired('vertical') ? 'required' : ''"
                            >
                                <v-select
                                    id="vertical"
                                    placeholder="Select Vertical"
                                    v-model="reimbursement.vertical"
                                    :options="verticalList"
                                    label="title"
                                    :reduce="(option) => option.title"
                                    :state="errors.length > 0 ? false:null"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Category *" label-for="category">
                            <validation-provider
                                #default="{ errors }"
                                name="Category"
                                :rules="isFieldRequired('category') ? 'required' : ''"
                            >
                                <v-select
                                    id="category"
                                    placeholder="Select Category"
                                    v-model="reimbursement.category"
                                    :options="categoryList"
                                    label="title"
                                    :reduce="(option) => option.title"
                                    :state="errors.length > 0 ? false:null"
                                    @open="ensureCategories"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4" v-if="isSubCategoryVisible">
                        <b-form-group label="Sub-Category *" label-for="sub-category">
                            <validation-provider
                                #default="{ errors }"
                                name="Sub-Category"
                                :rules="isSubCategoryRequired ? 'required' : ''"
                            >
                                <v-select
                                    id="sub-category"
                                    placeholder="Select Sub-Category"
                                    v-model="reimbursement.subCategory"
                                    :options="filteredSubCategories"
                                    label="title"
                                    :reduce="(option) => option.title"
                                    :state="errors.length > 0 ? false:null"
                                    @open="ensureCategories"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Amount *" label-for="amount">
                            <validation-provider
                                #default="{ errors }"
                                name="Amount"
                                :rules="isFieldRequired('amount') ? 'required|double' : 'double'"
                            >
                                <b-form-input
                                    id="amount"
                                    v-model="reimbursement.amount"
                                    :state="errors.length > 0 ? false:null"
                                    placeholder="Amount"
                                    type="number"
                                    step=".01"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Payment Mode *" label-for="payment-mode">
                            <validation-provider
                                #default="{ errors }"
                                name="Payment Mode"
                                :rules="isFieldRequired('paymentMode') ? 'required' : ''"
                            >
                                <v-select
                                    id="payment-mode"
                                    placeholder="Select Payment Mode"
                                    v-model="reimbursement.paymentMode"
                                    :options="paymentModeList"
                                    label="title"
                                    :reduce="(option) => option.title"
                                    :state="errors.length > 0 ? false:null"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Date *" label-for="expense-date">
                            <validation-provider
                                #default="{ errors }"
                                name="Date"
                                :rules="isFieldRequired('expenseDate') ? 'required' : ''"
                            >
                                <flat-pickr
                                    class="form-control"
                                    id="expense-date"
                                    v-model="reimbursement.expenseDate"
                                    placeholder="YYYY-MM-DD"
                                    :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Location *" label-for="location">
                            <validation-provider
                                #default="{ errors }"
                                name="Location"
                                :rules="isFieldRequired('location') ? 'required' : ''"
                            >
                                <v-select
                                    id="location"
                                    placeholder="Select Location"
                                    v-model="reimbursement.location"
                                    :options="locationList"
                                    label="title"
                                    :reduce="(option) => option.title"
                                    :state="errors.length > 0 ? false:null"
                                    @open="ensureLocations"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="Invoice No" label-for="invoice-no">
                            <b-form-input
                                id="invoice-no"
                                v-model="reimbursement.invoiceNo"
                                placeholder="Invoice No (optional)"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="GST Invoice?" label-for="is-gst">
                            <v-select
                                id="is-gst"
                                placeholder="GST Yes/No"
                                v-model="reimbursement.isGSTInvoice"
                                :options="booleanList"
                                label="title"
                                :reduce="(option) => option.value"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4" v-if="reimbursement.isGSTInvoice">
                        <b-form-group label="GST Number *" label-for="gst-number">
                            <validation-provider
                                #default="{ errors }"
                                name="GST Number"
                                rules="required"
                            >
                                <v-select
                                    id="gst-number"
                                    placeholder="Select GST Number"
                                    v-model="reimbursement.gstNumber"
                                    :options="gstNumberList"
                                    label="title"
                                    :reduce="(option) => option.title"
                                    :state="errors.length > 0 ? false:null"
                                    @open="ensureGstNumbers"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4" v-if="showTDSPrompt">
                        <b-form-group label="TDS Applicable?" label-for="is-tds">
                            <v-select
                                id="is-tds"
                                placeholder="TDS Yes/No"
                                v-model="reimbursement.isTDSDeduction"
                                :options="booleanList"
                                label="title"
                                :reduce="(option) => option.value"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4" v-if="showTDSPrompt && reimbursement.isTDSDeduction">
                        <b-form-group label="TDS Amount *" label-for="tds-amount">
                            <validation-provider
                                #default="{ errors }"
                                name="TDS Amount"
                                rules="required|double"
                            >
                                <b-form-input
                                    id="tds-amount"
                                    v-model="reimbursement.tdsAmount"
                                    :state="errors.length > 0 ? false:null"
                                    placeholder="TDS Amount"
                                    type="number"
                                    step=".01"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="8">
                        <b-form-group label-for="supporting-documents">
                            <template #label>
                                Supporting Documents <span class="text-danger">*</span>
                            </template>
                            <small class="text-muted d-block mb-50">Required — PDF, Excel, Word, or image. Max {{ REIMBURSEMENT_MAX_FILE_SIZE_LABEL }}. Keep the existing file or upload a replacement.</small>
                            <p v-if="existingFilePath" class="small text-muted mb-50">
                                Current:
                                <a href="#" @click.prevent="openAttachment">View attachment</a>
                                <span v-if="existingFileName"> ({{ existingFileName }})</span>
                            </p>
                            <b-form-file
                                placeholder="Choose a file or drop it here..."
                                drop-placeholder="Drop file here..."
                                id="supporting-documents"
                                accept="application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, text/plain, application/rtf, text/markdown, image/*, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                v-model="file"
                                :state="attachmentRequiredError ? false : null"
                            />
                            <small v-if="fileSizeError" class="text-danger d-block mt-50">{{ fileSizeError }}</small>
                            <small v-else-if="attachmentRequiredError" class="text-danger d-block mt-50">{{ attachmentRequiredError }}</small>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12">
                        <b-form-group label-for="comment" label="Description / Comment *">
                            <validation-provider
                                #default="{ errors }"
                                name="Description / Comment"
                                :rules="isFieldRequired('comment') ? 'required' : ''"
                            >
                                <b-form-textarea
                                    id="reimbursement-comment"
                                    placeholder="Describe what this reimbursement is for"
                                    v-model="reimbursement.comment"
                                    :state="errors.length > 0 ? false:null"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12">
                        <b-alert :show="!!submitError" variant="danger" class="p-1">
                            {{ submitError }}
                        </b-alert>
                    </b-col>
                    <b-col cols="12" class="reimbursement-form-actions">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            type="submit"
                            variant="primary"
                            class="mr-sm-1"
                            :disabled="submitting"
                            @click="submitForm"
                        >
                            {{ submitting ? 'Saving...' : 'Save Changes' }}
                        </b-button>
                        <b-button
                            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                            variant="outline-secondary"
                            @click="$router.push({ name: 'apps-reimbursement' })"
                        >
                            Cancel
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </validation-observer>
    </b-card>
    </div>
</template>
<script>
import {
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BCard,
    BRow,
    BCol,
    BFormTextarea,
    BButton,
    BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import clientService from '@/services/client-service'
import locationService from '@/services/location-service'
import AdminConfigService from '@/services/admin-config-service'
import invoiceConfigService from '@/services/invoice-config-service'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import reimbursementService, { formatReimbursementApiError } from '@/services/reimbursement-service'
import {
    getEditDenialReason,
    normalizeReimbursementStatus,
    REIMBURSEMENT_STATUSES,
} from '@/utils/reimbursement-permissions'
import {
    isFieldRequired,
    resolveOptions,
    getSubCategoryOptions,
    extractExpenseSubCategoryRows,
    categoryRequiresTDSPrompt,
    USE_LIVE_PAYMENT_MODE_LIST,
    PAYMENT_MODE_OPTIONS,
    USE_LIVE_CLIENT_LIST,
    ACCOUNT_NAME_OPTIONS,
    USE_LIVE_LOCATION_LIST,
    LOCATION_OPTIONS,
    USE_LIVE_CATEGORY_LIST,
    CATEGORY_OPTIONS,
    VERTICAL_OPTIONS,
    USE_LIVE_GST_NUMBER_LIST,
    GST_NUMBER_OPTIONS,
    SEND_BUSINESS_UNIT,
    BUSINESS_UNIT_DEFAULT,
    SEND_GST_AMOUNT,
    REIMBURSEMENT_FILE_FIELD,
    REIMBURSEMENT_MAX_FILE_SIZE_LABEL,
    getReimbursementFileSizeError,
    getReimbursementAttachmentRequiredError,
    getItemTitleRequiredError,
    getReimbursementFilePath,
    getReimbursementFileName,
} from '@/utils/reimbursement-config'

export default {
    components: {
        BButton,
        BForm,
        BFormGroup,
        BCard,
        BRow,
        BCol,
        BFormTextarea,
        BFormInput,
        BFormFile,
        BAlert,
        ValidationProvider,
        ValidationObserver,
        vSelect,
        flatPickr,
    },
    data() {
        return {
            REIMBURSEMENT_MAX_FILE_SIZE_LABEL,
            claimId: null,
            originalStatus: null,
            loading: true,
            loadError: null,
            userData: {},
            reimbursement: {
                itemName: null,
                accountName: null,
                vertical: null,
                invoiceNo: null,
                category: null,
                subCategory: null,
                expenseDate: null,
                paymentMode: null,
                isGSTInvoice: false,
                isTDSDeduction: false,
                amount: null,
                gstNumber: null,
                tdsAmount: null,
                location: null,
                comment: null,
            },
            existingFilePath: null,
            liveAccountsList: [],
            liveCategoryList: [],
            liveSubCategoryList: [],
            liveLocationList: [],
            livePaymentModeList: [],
            liveGstNumberList: [],
            booleanList: [
                { title: 'YES', value: true },
                { title: 'NO', value: false },
            ],
            file: null,
            submitting: false,
            submitError: null,
            attachmentValidationAttempted: false,
            itemTitleValidationAttempted: false,
            configLoaded: {
                clients: false,
                categories: false,
                locations: false,
                gst: false,
            },
            configLoading: {
                clients: false,
                categories: false,
                locations: false,
                gst: false,
            },
        }
    },
    directives: {
        Ripple,
    },
    computed: {
        accountsList() {
            return resolveOptions(USE_LIVE_CLIENT_LIST, this.liveAccountsList, ACCOUNT_NAME_OPTIONS.map(title => ({ title })))
        },
        verticalList() {
            return VERTICAL_OPTIONS.map(title => ({ title }))
        },
        categoryList() {
            return resolveOptions(
                USE_LIVE_CATEGORY_LIST,
                this.liveCategoryList,
                CATEGORY_OPTIONS.map(title => ({ title })),
            )
        },
        locationList() {
            return resolveOptions(USE_LIVE_LOCATION_LIST, this.liveLocationList, LOCATION_OPTIONS.map(title => ({ title })))
        },
        paymentModeList() {
            return resolveOptions(USE_LIVE_PAYMENT_MODE_LIST, this.livePaymentModeList, PAYMENT_MODE_OPTIONS.map(title => ({ title })))
        },
        gstNumberList() {
            return resolveOptions(
                USE_LIVE_GST_NUMBER_LIST,
                this.liveGstNumberList,
                GST_NUMBER_OPTIONS.map(title => ({ title })),
            )
        },
        filteredSubCategories() {
            return getSubCategoryOptions(this.reimbursement.category, this.liveSubCategoryList).map(title => ({ title }))
        },
        isSubCategoryVisible() {
            return !!this.reimbursement.category
                && (this.filteredSubCategories.length > 0 || !!this.reimbursement.subCategory)
        },
        itemTitleError() {
            if (!this.itemTitleValidationAttempted) return null
            return getItemTitleRequiredError(this.reimbursement.itemName)
        },
        isSubCategoryRequired() {
            return isFieldRequired('subCategory') && this.isSubCategoryVisible
        },
        showTDSPrompt() {
            return categoryRequiresTDSPrompt(this.reimbursement.category, this.reimbursement.subCategory)
        },
        fileSizeError() {
            return getReimbursementFileSizeError(this.file)
        },
        attachmentRequiredError() {
            if (!this.attachmentValidationAttempted) return null
            return getReimbursementAttachmentRequiredError(this.file, this.existingFilePath)
        },
        existingFileName() {
            return getReimbursementFileName(this.existingFilePath)
        },
    },
    watch: {
        'reimbursement.category'(newVal, oldVal) {
            if (oldVal != null && newVal !== oldVal) {
                this.reimbursement.subCategory = null
            }
        },
        'reimbursement.isGSTInvoice'(newVal) {
            if (!newVal) {
                this.reimbursement.gstNumber = null
            }
        },
    },
    created() {
        this.userData = JSON.parse(localStorage.getItem('userData')) || {}
        this.claimId = this.$route.params.id
    },
    mounted() {
        this.loadClaim()
    },
    methods: {
        isFieldRequired,
        ensureClients() {
            if (this.configLoaded.clients || this.configLoading.clients) return
            this.configLoading.clients = true
            clientService.getClients().then(res => {
                this.liveAccountsList = res.data || []
                this.configLoaded.clients = true
            }).finally(() => {
                this.configLoading.clients = false
            })
        },
        ensureCategories() {
            if (this.configLoaded.categories || this.configLoading.categories) return
            this.configLoading.categories = true
            AdminConfigService.getConfigExpense().then(res => {
                if (res.status === 'OK') {
                    this.liveCategoryList = res.data.category || []
                    this.liveSubCategoryList = extractExpenseSubCategoryRows(res.data)
                    this.livePaymentModeList = res.data.payment_mode || []
                }
                this.configLoaded.categories = true
            }).finally(() => {
                this.configLoading.categories = false
            })
        },
        ensureLocations() {
            if (this.configLoaded.locations || this.configLoading.locations) return
            this.configLoading.locations = true
            locationService.getAll().then(res => {
                this.liveLocationList = res.data || []
                this.configLoaded.locations = true
            }).finally(() => {
                this.configLoading.locations = false
            })
        },
        ensureGstNumbers() {
            if (this.configLoaded.gst || this.configLoading.gst) return
            this.configLoading.gst = true
            invoiceConfigService.getSattvaCompanyDetails().then(res => {
                if (res && res.status === 'OK' && res.data != null) {
                    const rows = Array.isArray(res.data) ? res.data : [res.data]
                    const gstins = [...new Set(
                        rows
                            .map(row => (row && (row.gstin || row.gst || row.gst_no || '')).toString().trim().toUpperCase())
                            .filter(Boolean),
                    )]
                    this.liveGstNumberList = gstins.map(title => ({ title }))
                }
                this.configLoaded.gst = true
            }).catch(() => {
                this.configLoaded.gst = true
            }).finally(() => {
                this.configLoading.gst = false
            })
        },
        validateItemTitle() {
            this.itemTitleValidationAttempted = true
            return getItemTitleRequiredError(this.reimbursement.itemName)
        },
        async openAttachment() {
            if (!this.existingFilePath) return
            const url = await reimbursementService.resolveAttachmentUrl(this.existingFilePath)
            if (url) {
                window.open(url, '_blank', 'noopener')
            } else {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Attachment unavailable',
                        text: 'Could not open the supporting document. It may not have been uploaded yet, or the download service is unavailable.',
                        icon: 'AlertCircleIcon',
                        variant: 'warning',
                    },
                })
            }
        },
        formatApiError(res) {
            return formatReimbursementApiError(
                res,
                'Failed to update reimbursement. Please try again or contact Admin.',
            )
        },
        populateForm(row) {
            this.originalStatus = normalizeReimbursementStatus(row)
            this.existingFilePath = getReimbursementFilePath(row)
            const gstNumber = row.gst_number || row.gstNumber || null
            this.reimbursement = {
                itemName: row.item || null,
                accountName: row.account || null,
                vertical: row.vertical || null,
                invoiceNo: row.invoice_no || null,
                category: row.category === 'null' ? null : (row.category || null),
                subCategory: row.sub_category === 'null' ? null : (row.sub_category || null),
                expenseDate: row.expense_date || null,
                paymentMode: row.payment_mode || null,
                isGSTInvoice: row.is_gst === true || row.is_gst === 'true',
                isTDSDeduction: row.is_tds === true || row.is_tds === 'true',
                amount: row.amount,
                gstNumber: gstNumber ? String(gstNumber).toUpperCase() : null,
                tdsAmount: row.tds_amount,
                location: row.location || null,
                comment: row.comment || null,
            }
        },
        async loadClaim() {
            this.loading = true
            this.loadError = null
            try {
                const res = await reimbursementService.getReimbursementById(this.claimId)
                this.loading = false
                if (res.status === 'OK' && res.data) {
                    const denialReason = getEditDenialReason(this.$can.bind(this), this.userData, res.data)
                    if (denialReason) {
                        this.loadError = denialReason
                        return
                    }
                    this.populateForm(res.data)
                    return
                }
                if (res.status === 401 || res.status === 403) {
                    window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
                    localStorage.clear()
                    this.$router.push({ name: 'auth-login' })
                    return
                }
                this.loadError = this.formatApiError(res)
            } catch (err) {
                this.loading = false
                this.loadError = this.formatApiError(err?.response?.data)
            }
        },
        buildUpdatePayload() {
            const item = String(this.reimbursement.itemName || '').trim()
            const payload = {
                item,
                account: this.reimbursement.accountName,
                vertical: this.reimbursement.vertical || '',
                invoice_no: this.reimbursement.invoiceNo || '',
                category: this.reimbursement.category || '',
                sub_category: this.reimbursement.subCategory || '',
                expense_date: this.reimbursement.expenseDate || '',
                amount: Number(this.reimbursement.amount),
                payment_mode: this.reimbursement.paymentMode,
                is_gst: !!this.reimbursement.isGSTInvoice,
                is_tds: !!this.reimbursement.isTDSDeduction,
                tds_amount: this.reimbursement.isTDSDeduction ? Number(this.reimbursement.tdsAmount) : 0,
                location: this.reimbursement.location,
                comment: this.reimbursement.comment,
            }
            if (this.reimbursement.isGSTInvoice && this.reimbursement.gstNumber) {
                payload.gst_number = this.reimbursement.gstNumber
            }
            if (SEND_GST_AMOUNT) {
                payload.gst_amount = 0
            }
            // business_unit is optional — omit entirely (do not send empty string).
            if (SEND_BUSINESS_UNIT) {
                const bu = (BUSINESS_UNIT_DEFAULT || '').trim()
                if (bu) payload.business_unit = bu
            }
            if (this.originalStatus === REIMBURSEMENT_STATUSES.REJECTED) {
                payload.status = REIMBURSEMENT_STATUSES.PENDING_L1
                payload.is_approved = false
            }
            return payload
        },
        submitForm() {
            this.submitError = null
            this.attachmentValidationAttempted = true
            const itemTitleError = this.validateItemTitle()
            this.$refs.reimbursementRules.validate().then(success => {
                if (itemTitleError) {
                    this.submitError = itemTitleError
                    return
                }
                if (!success) {
                    this.submitError = 'Please fill in all mandatory fields highlighted above before saving.'
                    return
                }
                const missingAttachment = getReimbursementAttachmentRequiredError(this.file, this.existingFilePath)
                if (missingAttachment) {
                    this.submitError = missingAttachment
                    return
                }
                const oversized = getReimbursementFileSizeError(this.file)
                if (oversized) {
                    this.submitError = oversized
                    return
                }

                this.submitting = true
                const useMultipart = !!this.file
                let request

                if (useMultipart) {
                    const formData = new FormData()
                    const payload = this.buildUpdatePayload()
                    Object.entries(payload).forEach(([key, value]) => {
                        formData.append(key, value == null ? '' : String(value))
                    })
                    formData.append(REIMBURSEMENT_FILE_FIELD, this.file)
                    request = reimbursementService.editReimbursement(this.claimId, formData)
                } else {
                    request = reimbursementService.updateReimbursement(this.claimId, this.buildUpdatePayload())
                }

                request.then(res => {
                    this.submitting = false
                    if (res.status === 'OK') {
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: this.originalStatus === REIMBURSEMENT_STATUSES.REJECTED
                                    ? 'Reimbursement updated and resubmitted for approval.'
                                    : 'Reimbursement updated successfully.',
                                icon: 'CheckIcon',
                                variant: 'success',
                                timeout: 5000,
                            },
                        })
                        this.$router.push({ name: 'apps-reimbursement' })
                    } else if (res.status === 401 || res.status === 403) {
                        window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
                        localStorage.clear()
                        this.$router.push({ name: 'auth-login' })
                    } else {
                        const errorMessage = this.formatApiError(res)
                        this.submitError = errorMessage
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: errorMessage,
                                icon: 'AlertTriangleIcon',
                                variant: 'danger',
                                timeout: 8000,
                            },
                        })
                    }
                }).catch(err => {
                    this.submitting = false
                    const errorMessage = this.formatApiError(err?.response?.data)
                    this.submitError = errorMessage
                    this.$toast({
                        component: ToastificationContent,
                        props: {
                            title: errorMessage,
                            icon: 'AlertTriangleIcon',
                            variant: 'danger',
                            timeout: 8000,
                        },
                    })
                })
            })
        },
    },
}
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style lang="scss" scoped>
// Navbar already shows pageTitle — reclaim the large content-wrapper gap
// (same pattern as Main Dashboard / Revenue). Scoped to this page only.
.reimbursement-edit-page {
    margin-top: -0.85rem;
}

::v-deep .v-select,
::v-deep .form-control {
    max-width: 100%;
}
.reimbursement-form-actions {
    .btn {
        min-height: 44px;
    }
    @media (max-width: 575.98px) {
        .btn {
            display: block;
            width: 100%;
        }
        .btn + .btn {
            margin-top: 0.75rem;
            margin-left: 0 !important;
        }
    }
}
</style>
